import _ from 'lodash'
import json from '@/lib/scoresConfig.json'

export const TOURNAMENT_NUMBERS = {
  CLASSIC: 8,
  SIGNALS: 11,
  CRYPTO_SIGNALS: 12,
}

export function isAppClassic() {
  return (
    window.location.hostname === 'numer.ai' ||
    window.location.hostname.startsWith('numerai') ||
    process.env.VUE_APP_TOURNAMENT === `${TOURNAMENT_NUMBERS.CLASSIC}`
  )
}

export function isAppSignals() {
  return (
    window.location.hostname.startsWith('signals') ||
    process.env.VUE_APP_TOURNAMENT === `${TOURNAMENT_NUMBERS.SIGNALS}`
  )
}

export function isAppCryptoSignals() {
  return (
    window.location.hostname.startsWith('crypto') ||
    process.env.VUE_APP_TOURNAMENT === `${TOURNAMENT_NUMBERS.CRYPTO_SIGNALS}`
  )
}

export function getTournamentNumber() {
  if (isAppClassic()) {
    return TOURNAMENT_NUMBERS.CLASSIC
  } else if (isAppSignals()) {
    return TOURNAMENT_NUMBERS.SIGNALS
  } else if (isAppCryptoSignals()) {
    return TOURNAMENT_NUMBERS.CRYPTO_SIGNALS
  } else {
    console.error('Unknown tournament!!!')
  }
}

export function isSoloPage(routerPath) {
  const excludedViews = ['/login', '/signup', '/reset', '/reset-password/u/']
  return excludedViews.some(view => routerPath.includes(view))
}

export function getTournamentScoresConfig(tournament_number, filterKey) {
  const config = json
  const scoresConfig = config[tournament_number.toString()]

  if (filterKey) {
    const filter = Object.entries(scoresConfig).filter(([, value]) => {
      return value[filterKey] === true
    })

    return Object.fromEntries(filter)
  }

  return scoresConfig
}

// returns a rounded string from a score
export function formatScore(_score, decimals = 4) {
  if (_score === '?' || _score === '-') {
    return null
  }

  if (!_score && _score != 0) {
    return null
  }

  if (Number(_score) === 0) {
    return '0.0000'
  }

  const rounded = _.round(_score, decimals)
  if (rounded === 0) {
    return '0.0000'
  }

  var roundedStr = '' + rounded
  if (Math.abs(rounded) < 1 && roundedStr.length > 1 && roundedStr.length < 6) {
    roundedStr = _.padEnd(roundedStr, 6, '0')
  } else if (rounded < 0 && roundedStr.length > 1 && roundedStr.length < 7) {
    roundedStr = _.padEnd(roundedStr, 7, '0')
  }

  return roundedStr || '-'
}

export function formatPercent(_percent, _toFixed) {
  if (parseFloat(_percent)) {
    if (Math.abs(parseFloat(_percent)) < 1 && parseFloat(_percent) != 0) {
      let tofixed = 2
      if (_toFixed) {
        tofixed = _toFixed
      }
      return parseFloat(_percent).toFixed(tofixed) + '%'
    } else {
      let tofixed = 1
      if (_toFixed) {
        tofixed = _toFixed
      }
      return parseFloat(_percent).toFixed(tofixed) + '%'
    }
  }

  return '-'
}

export function formatPercentile(_percentile) {
  var percentile = parseFloat(_percentile)
  if (percentile) {
    return Math.round(percentile * 1000) / 10
  }

  return '-'
}

export function makeNumberStyle(_val) {
  if (!parseFloat(_val)) {
    return { 'null-number': true }
  }

  if (parseFloat(_val) > 0) {
    return { 'color-positive': true }
  }

  if (parseFloat(_val) < 0) {
    return { 'color-negative': true }
  }
}

// TODO: Could offer an optional precision argument
// Should this return `-` when false/invalid?
export function formatCurrency(rawCurrency) {
  if (!rawCurrency && rawCurrency != 0) {
    return null
  }

  const currency = parseFloat(rawCurrency)
  if (Math.abs(currency) < 1 && currency !== 0) {
    // If number is a fraction, use toPrecision
    return currency.toPrecision(2).toLocaleString('en-US')
  }
  // else round
  const decimals = currency >= 100 ? 0 : 3
  return _.round(currency, decimals).toLocaleString('en-US')
}

export function removeMinusSign(_string) {
  _string = '' + _string

  if (_string.startsWith('-')) {
    return _string.substring(1)
  } else {
    return _string
  }
}

export function sendLoadTimeToPlausible(name) {
  window.performance.measure(name, 'mark1')
  var loadTime = window.performance.getEntriesByType('measure')[0].duration
  var loadTimeBucket
  if (loadTime < 1000) {
    loadTimeBucket = '0 - 1'
  } else if (loadTime < 3000) {
    loadTimeBucket = '1 - 3'
  } else if (loadTime < 7000) {
    loadTimeBucket = '3 - 7'
  } else if (loadTime < 15000) {
    loadTimeBucket = '7 - 15'
  } else if (loadTime < 30000) {
    loadTimeBucket = '15 - 30'
  } else if (loadTime < 60000) {
    loadTimeBucket = '30 - 60'
  } else {
    loadTimeBucket = '60 +'
  }
  window.plausible('Website Load Times', {
    props: { [name]: Math.round(loadTime / 100) / 10 },
  })
  window.plausible('Website Load Times Bucketed', {
    props: { [name]: loadTimeBucket },
  })
}

export function formatStakeType(
  corrMultiplier,
  mmcMultiplier,
  tcMultiplier,
  roundNumber = null,
) {
  var multipliers = []
  if (corrMultiplier) {
    // this is a hack, for signals show multiplier as FNCv4 instead of CORR for
    // rounds 499 and above. for rounds <= 278, just show CORR cause those were
    // before 20d targets
    if (isAppSignals()) {
      if (roundNumber >= 499 || roundNumber == null) {
        multipliers.push(corrMultiplier + '×FNCv4')
      } else if (roundNumber <= 278) {
        multipliers.push(corrMultiplier + '×CORR')
      } else {
        multipliers.push(corrMultiplier + '×CORR20')
      }
    } else if (isAppClassic()) {
      if (roundNumber >= 484 || roundNumber == null) {
        multipliers.push(corrMultiplier + '×CORR20v2')
      } else {
        multipliers.push(corrMultiplier + '×CORR20')
      }
    } else if (isAppCryptoSignals()) {
      multipliers.push(corrMultiplier + '×CORR')
    }
  }
  if (mmcMultiplier) {
    multipliers.push(mmcMultiplier + '×MMC')
  }
  if (tcMultiplier) {
    multipliers.push(tcMultiplier + '×TC')
  }
  return multipliers.join(' ')
}

export function ordinalSuffixOf(i) {
  var j = i % 10,
    k = i % 100
  if (j == 1 && k != 11) {
    return i + 'st'
  }
  if (j == 2 && k != 12) {
    return i + 'nd'
  }
  if (j == 3 && k != 13) {
    return i + 'rd'
  }
  return i + 'th'
}

export function shortenAddress(_address) {
  // TODO: Could do address checking here

  if (_address && _address.length > 15) {
    var ret =
      _address.substr(0, 5) + '...' + _address.substr(_address.length - 5, 5)
    return ret
  }

  return _address
}

// Our backend appends a 13 character hash to uploaded submission filenames.
// These should not be shown to users.
export function trimFilenameHash(filename) {
  if (!filename) {
    return ''
  }
  const tempArr = filename.split('.')
  const extension =
    tempArr.length > 1 && tempArr[tempArr.length - 1].length <= 4
      ? `.${tempArr[tempArr.length - 1]}`
      : ''
  const hashLength = 13
  return [
    ...filename.slice(0, filename.length - (extension.length + hashLength)),
    ...extension,
  ].join('')
}

// Assumes YYYYMMDD format
export function getDateFromInt(number) {
  const dateString = number.toString()
  return [
    dateString.slice(0, 4),
    '-',
    dateString.slice(4, 6),
    '-',
    dateString.slice(6, 8),
  ].join('')
}

export function getDefaultSeasonIndex(seasonStatuses) {
  // if the most recent season doesn't have scores/ranks yet, select the previous season
  // but do show most recent season if user didn't qualify for previous season
  let selectedIndex = seasonStatuses.length - 1
  let mostRecentSeason = seasonStatuses[seasonStatuses.length - 1]
  if (!mostRecentSeason.hasScores) {
    if (seasonStatuses.length === 1) {
      return 0
    }

    // select previous season
    selectedIndex = seasonStatuses.length - 2

    // check if user qualified for the previous season.
    // if user is not logged in, numQualifyingSubmissions will be null
    let previousSeason = seasonStatuses[selectedIndex]
    if (
      previousSeason &&
      previousSeason.numQualifyingSubmissions != null &&
      previousSeason.numQualifyingSubmissions < 20
    ) {
      // not qualified, select most recent season
      selectedIndex = seasonStatuses.length - 1
    }
  }
  return selectedIndex
}

function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1)
}

export default {
  isAppClassic,
  isAppSignals,
  isAppCryptoSignals,
  isSoloPage,
  getTournamentNumber,
  getTournamentScoresConfig,
  TOURNAMENT_NUMBERS,
  formatScore,
  formatCurrency,
  formatPercent,
  removeMinusSign,
  sendLoadTimeToPlausible,
  formatStakeType,
  trimFilenameHash,
  getDateFromInt,
  getDefaultSeasonIndex,
  capitalizeFirstLetter,
}
